import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, Package, TrendingUp, DollarSign, Clock, RotateCw } from 'lucide-react';
import apiService from '../../services/ApiService';
import { useApp } from '../../context/AppContext';
import { hasPermission } from '../../utils/roleHelpers';

const FONT     = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const BG       = '#F2F2F7';
const INK      = '#1C1C1E';
const MUTED    = '#8E8E93';
const SECONDARY = '#3C3C43';
const CARD     = { background: '#fff', borderRadius: 18, boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.07)' };
const SYS_GREEN  = '#34C759';
const SYS_RED    = '#FF3B30';
const SYS_BLUE   = '#007AFF';
const SYS_ORANGE = '#FF9500';
const BRAND      = '#84CC16';

function fmt(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusBadge(status) {
  const map = {
    paid:    [SYS_GREEN,  'rgba(52,199,89,0.12)'],
    pending: [SYS_ORANGE, 'rgba(255,149,0,0.12)'],
    overdue: [SYS_RED,    'rgba(255,59,48,0.12)'],
  };
  const [color, bg] = map[status] || [MUTED, 'rgba(142,142,147,0.12)'];
  return (
    <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600, color, background: bg, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: FONT }}>
      {status}
    </span>
  );
}

function KpiCard({ label, value, sub, valueColor, icon: Icon, iconColor }) {
  return (
    <div style={{ ...CARD, padding: '20px 22px' }}>
      {Icon && (
        <div style={{ width: 34, height: 34, borderRadius: 10, background: `${iconColor || BRAND}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
          <Icon size={17} color={iconColor || BRAND} />
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, fontFamily: FONT }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: valueColor || INK, fontVariantNumeric: 'tabular-nums', fontFamily: FONT, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: MUTED, marginTop: 5, fontFamily: FONT }}>{sub}</div>}
    </div>
  );
}

function BarRow({ label, value, total, color, sub }) {
  const p = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: SECONDARY, fontWeight: 500 }}>{label}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {sub && <span style={{ fontSize: 11, color: MUTED }}>{sub}</span>}
          <span style={{ fontSize: 13, fontWeight: 700, color: INK }}>{fmt(value)}</span>
        </div>
      </div>
      <div style={{ height: 4, background: '#E5E5EA', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', background: color, width: `${p}%`, borderRadius: 2, transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)' }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { currentUser } = useApp();
  const [inventory, setInventory]     = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [recurring, setRecurring]     = useState([]);
  const [loading, setLoading]         = useState(true);

  const canViewInventory = hasPermission(currentUser, 'inventory');
  const canViewFinancial = hasPermission(currentUser, 'financial');

  const load = useCallback(async () => {
    setLoading(true);
    const promises = [];
    if (canViewInventory) promises.push(apiService.getInventory().then(d => setInventory(d)).catch(() => {}));
    if (canViewFinancial) promises.push(
      apiService.getTransactions().then(d => setTransactions(d)).catch(() => {}),
      apiService.getRecurring().then(d => setRecurring(d)).catch(() => {}),
    );
    await Promise.all(promises);
    setLoading(false);
  }, [canViewInventory, canViewFinancial]);

  useEffect(() => { load(); }, [load]);

  const now        = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const daysElapsed = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  const lowStockItems    = inventory.filter(i => i.quantity <= i.minQuantity && i.minQuantity > 0);
  const outOfStock       = inventory.filter(i => i.quantity === 0);
  const monthSales       = transactions.filter(t => t.type === 'sale' && t.status === 'paid' && t.date >= monthStart);
  const monthExpenses    = transactions.filter(t => t.type === 'expense' && t.date >= monthStart);
  const monthRevenue     = monthSales.reduce((s, t) => s + t.amount, 0);
  const monthExp         = monthExpenses.reduce((s, t) => s + t.amount, 0);
  const monthlyOverhead  = recurring.filter(r => r.active !== false).reduce((s, r) => s + (r.frequency === 'monthly' ? r.amount : r.amount / 12), 0);
  const netProfit        = monthRevenue - monthExp - monthlyOverhead;
  const pending          = transactions.filter(t => t.status === 'pending' || t.status === 'overdue');
  const pendingTotal     = pending.reduce((s, t) => s + t.amount, 0);
  const totalCostValue   = inventory.reduce((s, i) => s + i.quantity * i.costPrice, 0);
  const totalRetailValue = inventory.reduce((s, i) => s + i.quantity * i.sellingPrice, 0);
  const potentialProfit  = totalRetailValue - totalCostValue;
  const avgMargin        = totalRetailValue > 0 ? ((potentialProfit / totalRetailValue) * 100) : 0;

  const projectedRevenue = daysElapsed > 0 && monthRevenue > 0 ? (monthRevenue / daysElapsed) * daysInMonth : null;

  const recentTransactions = [...transactions]
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    .slice(0, 6);

  // Top items by retail value
  const topItems = [...inventory]
    .filter(i => i.quantity > 0 && i.sellingPrice > 0)
    .sort((a, b) => (b.quantity * b.sellingPrice) - (a.quantity * a.sellingPrice))
    .slice(0, 6);

  // Category breakdown by retail value
  const catMap = {};
  inventory.forEach(i => {
    const cat = i.category || 'Other';
    if (!catMap[cat]) catMap[cat] = { retail: 0, count: 0 };
    catMap[cat].retail += i.quantity * i.sellingPrice;
    catMap[cat].count++;
  });
  const catList = Object.entries(catMap).sort((a, b) => b[1].retail - a[1].retail).slice(0, 6);

  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, background: BG, color: MUTED, fontSize: 14, fontFamily: FONT }}>
      Loading…
    </div>
  );

  return (
    <div style={{ padding: '24px 28px', overflowY: 'auto', height: '100%', background: BG, fontFamily: FONT, boxSizing: 'border-box' }}>

      {/* Greeting */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: INK, letterSpacing: '-0.02em' }}>
          {greeting}, {currentUser?.name?.split(' ')[0]}
        </div>
        <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>
          {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · Day {daysElapsed} of {daysInMonth}
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 18 }}>
        {canViewInventory && (
          <>
            <KpiCard label="Inventory Items" value={inventory.length} sub={`${inventory.reduce((s, i) => s + i.quantity, 0).toLocaleString()} units`} valueColor={BRAND} icon={Package} iconColor={BRAND} />
            <KpiCard label="Low Stock" value={lowStockItems.length} sub={`${outOfStock.length} out of stock`} valueColor={lowStockItems.length > 0 ? SYS_ORANGE : SYS_GREEN} icon={AlertTriangle} iconColor={lowStockItems.length > 0 ? SYS_ORANGE : SYS_GREEN} />
            <KpiCard label="Retail Value" value={fmt(totalRetailValue)} sub={`${avgMargin.toFixed(1)}% avg margin`} valueColor={SYS_GREEN} icon={TrendingUp} iconColor={SYS_GREEN} />
          </>
        )}
        {canViewFinancial && (
          <>
            <KpiCard label="Month Revenue" value={fmt(monthRevenue)} sub={projectedRevenue ? `→ ${fmt(projectedRevenue)} projected` : 'paid this month'} valueColor={SYS_GREEN} icon={DollarSign} iconColor={SYS_GREEN} />
            <KpiCard label="Net Profit" value={fmt(netProfit)} sub={`${fmt(monthlyOverhead)}/mo overhead`} valueColor={netProfit >= 0 ? BRAND : SYS_RED} icon={TrendingUp} iconColor={netProfit >= 0 ? BRAND : SYS_RED} />
            <KpiCard label="Pending" value={fmt(pendingTotal)} sub={`${pending.length} unpaid invoices`} valueColor={pending.length > 0 ? SYS_ORANGE : MUTED} icon={Clock} iconColor={SYS_ORANGE} />
          </>
        )}
      </div>

      {/* Middle row: Low stock + Transactions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>

        {/* Low stock */}
        {canViewInventory && (
          <div style={{ ...CARD, padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <AlertTriangle size={14} color={SYS_ORANGE} />
                <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>Low Stock Alerts</span>
              </div>
              {lowStockItems.length > 0 && (
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: 'rgba(255,149,0,0.12)', color: SYS_ORANGE }}>{lowStockItems.length}</span>
              )}
            </div>
            {lowStockItems.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0', gap: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(52,199,89,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Package size={18} color={SYS_GREEN} />
                </div>
                <span style={{ fontSize: 13, color: MUTED }}>All items are well stocked</span>
              </div>
            ) : (
              lowStockItems.slice(0, 5).map((item, i) => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < Math.min(lowStockItems.length, 5) - 1 ? '1px solid #F9F9F9' : 'none' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: item.quantity === 0 ? 'rgba(255,59,48,0.1)' : 'rgba(255,149,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <AlertTriangle size={12} color={item.quantity === 0 ? SYS_RED : SYS_ORANGE} />
                  </div>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: item.quantity === 0 ? SYS_RED : SYS_ORANGE, flexShrink: 0 }}>{item.quantity}</span>
                  <span style={{ fontSize: 10, color: MUTED, flexShrink: 0 }}>/ {item.minQuantity}</span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Recent transactions */}
        {canViewFinancial && (
          <div style={{ ...CARD, padding: '20px 22px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 14 }}>Recent Transactions</div>
            {recentTransactions.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0', gap: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#F2F2F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DollarSign size={18} color={MUTED} />
                </div>
                <span style={{ fontSize: 13, color: MUTED }}>No transactions yet</span>
                <span style={{ fontSize: 11, color: MUTED }}>Go to Financial to add your first sale</span>
              </div>
            ) : (
              recentTransactions.map((t, i) => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: i < recentTransactions.length - 1 ? '1px solid #F9F9F9' : 'none' }}>
                  <div style={{ minWidth: 0, paddingRight: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</div>
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>{t.date} · {statusBadge(t.status)}</div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, flexShrink: 0, fontVariantNumeric: 'tabular-nums', color: t.type === 'expense' ? SYS_RED : t.type === 'refund' ? SYS_ORANGE : SYS_GREEN }}>
                    {t.type === 'expense' ? '−' : '+'}{fmt(t.amount)}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Bottom row: Category breakdown + Top items */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>

        {/* Category breakdown by value */}
        {canViewInventory && catList.length > 0 && (
          <div style={{ ...CARD, padding: '20px 22px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 16 }}>Inventory by Category</div>
            {catList.map(([cat, v]) => (
              <BarRow key={cat} label={cat} value={v.retail} total={totalRetailValue} color={BRAND} sub={`${v.count} SKU${v.count !== 1 ? 's' : ''}`} />
            ))}
          </div>
        )}

        {/* Top items by retail value */}
        {canViewInventory && topItems.length > 0 && (
          <div style={{ ...CARD, padding: '20px 22px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 16 }}>Top Items by Value</div>
            {topItems.map((item, i) => {
              const val = item.quantity * item.sellingPrice;
              const maxVal = topItems[0].quantity * topItems[0].sellingPrice;
              return (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: i === 0 ? '#FEF3C7' : BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: i === 0 ? SYS_ORANGE : MUTED, flexShrink: 0 }}>{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 12, color: SECONDARY, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}>{item.name}{item.side ? ` (${item.side})` : ''}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: SYS_BLUE }}>{fmt(val)}</span>
                    </div>
                    <div style={{ height: 3, background: '#E5E5EA', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: i === 0 ? SYS_ORANGE : SYS_BLUE, width: `${maxVal > 0 ? (val / maxVal) * 100 : 0}%`, borderRadius: 2 }} />
                    </div>
                    <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>{item.quantity} units · {fmt(item.sellingPrice)} each</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Inventory value footer + Overhead summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>

        {canViewInventory && (
          <div style={{ ...CARD, padding: '20px 24px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 14 }}>Inventory Valuation</div>
            {[
              { label: 'Cost Value', value: fmt(totalCostValue), color: SECONDARY },
              { label: 'Retail Value', value: fmt(totalRetailValue), color: BRAND },
              { label: 'Potential Profit', value: fmt(potentialProfit), color: SYS_GREEN },
              { label: 'Avg Margin', value: `${avgMargin.toFixed(1)}%`, color: avgMargin >= 30 ? SYS_GREEN : avgMargin >= 15 ? SYS_ORANGE : SYS_RED },
            ].map(({ label, value, color }, i, arr) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < arr.length - 1 ? '1px solid #F9F9F9' : 'none' }}>
                <span style={{ fontSize: 13, color: SECONDARY }}>{label}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {canViewFinancial && recurring.filter(r => r.active !== false).length > 0 && (
          <div style={{ ...CARD, padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
              <RotateCw size={14} color={SYS_ORANGE} />
              <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>Fixed Overhead</span>
            </div>
            {recurring.filter(r => r.active !== false).slice(0, 5).map((r, i, arr) => {
              const mo = r.frequency === 'monthly' ? r.amount : r.amount / 12;
              return (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < arr.length - 1 ? '1px solid #F9F9F9' : 'none' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: INK }}>{r.name}</div>
                    <div style={{ fontSize: 10, color: MUTED }}>{r.category}</div>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: SYS_ORANGE, fontVariantNumeric: 'tabular-nums' }}>{fmt(mo)}/mo</span>
                </div>
              );
            })}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #E5E5EA', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: MUTED }}>Total monthly overhead</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: SYS_RED, fontVariantNumeric: 'tabular-nums' }}>{fmt(monthlyOverhead)}</span>
            </div>
          </div>
        )}

        {canViewFinancial && recurring.filter(r => r.active !== false).length === 0 && (
          <div style={{ ...CARD, padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
              <RotateCw size={14} color={MUTED} />
              <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>Fixed Overhead</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0', gap: 6 }}>
              <span style={{ fontSize: 13, color: MUTED }}>No overhead items set up yet</span>
              <span style={{ fontSize: 11, color: MUTED }}>Add rent, utilities, subscriptions in Financial → Overhead</span>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
